// Read-only tools the full-duplex voice session can call.
//
// The Realtime model gets function calling over the WebRTC data channel so it
// can actually look at the workspace — repo, files, code search, brain, preview
// console — instead of guessing. Nothing here writes: no run_command, no
// write_file, no apply_edit. Code changes stay in the text chat, where the
// approval UI lives.
//
// `runVoiceTool` returns a short string. Voice output is spoken, so results are
// capped hard — a 3000-line file read is useless to listen to.

import { runTool } from './agentTools.js';
import { executeGithubProjects } from './githubTool.js';

const MAX_SPOKEN_RESULT = 1400;

/** Realtime GA tool shape: flat `{ type, name, description, parameters }`. */
export const VOICE_TOOL_DEFS = [
  {
    type: 'function',
    name: 'apolo_context',
    description:
      'Onde você está agora: projeto, pasta da sessão, branch git, repositório remoto e o que você consegue fazer. Chame ANTES de responder qualquer pergunta sobre "onde estamos", "que projeto é esse", "o que você pode fazer".',
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'git_status',
    description:
      'Estado do git na sessão: branch atual, arquivos modificados e o remoto no GitHub. Use para "o que mudou", "em que branch estamos", "tem coisa não commitada".',
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'list_files',
    description:
      'Lista os arquivos de UMA pasta do projeto. Use para explorar a estrutura. Ex: {"path":"src"}.',
    parameters: {
      type: 'object',
      properties: { path: { type: 'string', description: 'Pasta relativa à raiz. Vazio = raiz.' } },
      required: [],
    },
  },
  {
    type: 'function',
    name: 'read_file',
    description:
      'Lê um arquivo do projeto. Só leia o que precisa: use offset/limit para janelas de linhas.',
    parameters: {
      type: 'object',
      properties: {
        path: { type: 'string', description: 'Caminho relativo à raiz. Ex: "src/index.js".' },
        offset: { type: 'number', description: 'Linha inicial (1-based). Negativo lê o fim.' },
        limit: { type: 'number', description: 'Quantas linhas ler.' },
      },
      required: ['path'],
    },
  },
  {
    type: 'function',
    name: 'grep_search',
    description:
      'Procura um texto literal (nome de função, string, componente) em todo o projeto. Use quando souber o termo exato.',
    parameters: {
      type: 'object',
      properties: { pattern: { type: 'string', description: 'Texto a buscar.' } },
      required: ['pattern'],
    },
  },
  {
    type: 'function',
    name: 'semantic_search',
    description:
      'Busca por CONCEITO no código indexado, quando você não sabe o termo exato. Ex: {"query":"como o chat injeta regras aprendidas"}.',
    parameters: {
      type: 'object',
      properties: { query: { type: 'string', description: '2 a 6 palavras de conteúdo.' } },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'search_brain',
    description:
      "Busca no Brain do Pedro (vault Obsidian): decisões, notas e contexto de projetos que não estão no código. Use para \"o que a gente decidiu\", \"veja meu brain\".",
    parameters: {
      type: 'object',
      properties: { query: { type: 'string', description: 'Pergunta ou tópico.' } },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'preview_console',
    description:
      'Lê os erros e avisos do console do preview (o app rodando). Use para "tem erro na tela", "o preview quebrou".',
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'github_projects',
    description:
      'Lista os repositórios do GitHub do usuário (mais recentes primeiro): nome, descrição, linguagem, visibilidade e data. Use para "quais projetos eu tenho", "sobre o que é o repo X", entender no que ele trabalha. Filtre com {"q":"apolo"}.',
    parameters: {
      type: 'object',
      properties: {
        q: { type: 'string', description: 'filtro por nome/descrição (opcional)' },
        max: { type: 'number', description: 'quantos repos (padrão 12)' },
      },
      required: [],
    },
  },
];

/** Tools routed straight to the agent executor. The rest are voice-only. */
const AGENT_TOOLS = new Set([
  'list_files',
  'read_file',
  'grep_search',
  'semantic_search',
  'search_brain',
  'preview_console',
]);

export const VOICE_TOOL_NAMES = VOICE_TOOL_DEFS.map((t) => t.name);

function cap(text) {
  const s = String(text ?? '').trim();
  if (!s) return 'Sem resultado.';
  return s.length > MAX_SPOKEN_RESULT ? `${s.slice(0, MAX_SPOKEN_RESULT)}\n…(cortado)` : s;
}

function describeCapabilities() {
  return [
    'Nesta conversa por voz você LÊ: arquivos e pastas da sessão, busca literal e semântica no código,',
    'o Brain (Obsidian), o estado do git, os seus projetos no GitHub e o console do preview.',
    'Você NÃO edita arquivos, não roda comandos e não faz commit por voz — isso é no chat de texto.',
  ].join(' ');
}

/** @param {{ root?: string, sessionId?: string, api?: * }} ctx */
async function apoloContext({ root, sessionId, api }) {
  const lines = [];
  const folder = String(root || '').trim();
  lines.push(folder ? `Pasta da sessão: ${folder}` : 'Nenhuma pasta de sessão aberta.');
  if (sessionId) lines.push(`Sessão: ${sessionId}`);
  if (folder && api?.git) {
    const branch = await api.git.currentBranch({ root: folder }).catch(() => null);
    if (branch?.branch) lines.push(`Branch: ${branch.branch}`);
    const remote = await api.git.originRemote({ root: folder }).catch(() => null);
    if (remote?.url || remote?.remote) lines.push(`Remoto: ${remote.url || remote.remote}`);
  }
  lines.push(describeCapabilities());
  return lines.join('\n');
}

/** @param {{ root?: string, api?: * }} ctx */
async function gitStatus({ root, api }) {
  const folder = String(root || '').trim();
  if (!folder) return 'Nenhuma pasta de sessão aberta — não dá para ver o git.';
  if (!api?.git) return 'Git indisponível fora do app Electron.';
  const [branch, status, remote] = await Promise.all([
    api.git.currentBranch({ root: folder }).catch(() => null),
    api.git.status({ root: folder }).catch(() => null),
    api.git.originRemote({ root: folder }).catch(() => null),
  ]);
  const files = status?.files || status?.changes || [];
  const changed = Array.isArray(files) ? files.length : 0;
  return [
    `Branch: ${branch?.branch || 'desconhecida'}`,
    `Remoto: ${remote?.url || remote?.remote || 'nenhum'}`,
    changed
      ? `${changed} arquivo(s) com mudança não commitada.`
      : 'Working tree limpo — nada pendente.',
  ].join('\n');
}

/**
 * Execute one voice tool call. Always resolves with a short string; a thrown
 * tool must not kill the live audio session.
 *
 * @param {string} name
 * @param {object} args
 * @param {{ root?: string, sessionId?: string, signal?: AbortSignal }} ctx
 * @returns {Promise<string>}
 */
export async function runVoiceTool(name, args = {}, ctx = {}) {
  const tool = String(name || '');
  const api = typeof window !== 'undefined' ? /** @type {*} */ (window).apolo : null;
  try {
    if (tool === 'apolo_context') return cap(await apoloContext({ ...ctx, api }));
    if (tool === 'git_status') return cap(await gitStatus({ ...ctx, api }));
    // GitHub projects — Worker read (não precisa de pasta de sessão aberta).
    if (tool === 'github_projects') return cap(await executeGithubProjects(args));
    if (!AGENT_TOOLS.has(tool)) return `Ferramenta "${tool}" não está disponível por voz.`;
    if (!ctx.root && tool !== 'search_brain') {
      return 'Nenhuma pasta de sessão aberta — abra uma sessão de código primeiro.';
    }
    return cap(await runTool(tool, args, { root: ctx.root, sessionId: ctx.sessionId, signal: ctx.signal }));
  } catch (error) {
    return `Falhou: ${error?.message || error}`;
  }
}
